<template>
  <div class="container">
    <header>
      <h1><i class="ri-radar-line"></i> AI 每日精选
        <span v-if="isToday" class="today-tag">今天</span>
      </h1>
      <div class="viewing-date" v-if="selectedDate">
        <span v-if="isToday" class="viewing-label">今天</span>
        <span v-else class="viewing-label">当前查看</span>
        <span class="viewing-value">{{ formatDateFull(selectedDate) }}</span>
      </div>
    </header>

    <!-- 日期导航 -->
    <div class="date-nav">
      <a
        v-for="d in recentPills"
        :key="d"
        class="date-pill"
        :class="{ active: d === selectedDate }"
        @click="selectDate(d)"
      >{{ formatDatePill(d) }}</a>
      <input
        type="date"
        class="date-input"
        v-model="dateInput"
        :min="minDate"
        :max="maxDate"
        title="跳转到指定日期"
        @change="onDateInput"
      />
    </div>

    <!-- 快捷条 -->
    <div class="back-bar">
      <a class="back-link" @click="goToday"><i class="ri-calendar-line"></i> 今天</a>
    </div>

    <!-- 统计卡 -->
    <div class="stats">
      <div class="stat-card"><div class="num">{{ currentItems.length }}</div><div class="label">条目</div></div>
      <div class="stat-card"><div class="num">{{ sourceCount }}</div><div class="label">来源</div></div>
      <div class="stat-card"><div class="num">{{ total }}</div><div class="label">总计</div></div>
      <div class="stat-card"><div class="num">{{ latestDateShort || '-' }}</div><div class="label">最新</div></div>
    </div>

    <!-- 来源筛选 -->
    <div class="source-bar" v-if="sourceList.length">
      <span
        class="source-badge"
        :class="{ active: activeSource === '全部' }"
        @click="setSource('全部')"
      >全部</span>
      <span
        v-for="s in sourceList"
        :key="s"
        class="source-badge"
        :class="{ active: activeSource === s }"
        @click="setSource(s)"
      >{{ s }}</span>
    </div>

    <!-- 搜索 + 排序 -->
    <div class="controls">
      <input type="text" v-model="keyword" placeholder="搜索标题、副标题..." @input="onSearch" />
      <select v-model="sortBy" @change="rerender">
        <option value="newest">最新优先</option>
        <option value="hottest">最热优先</option>
      </select>
      <span
        class="source-badge fav-toggle"
        :class="{ active: favOnly }"
        @click="favOnly = !favOnly"
        title="只看我收藏的精选（收藏保存在本地浏览器）"
      ><i :class="favOnly ? 'ri-star-fill' : 'ri-star-line'"></i> 收藏<template v-if="favCount"> {{ favCount }}</template></span>
    </div>

    <!-- 收藏管理条（只看收藏时显示） -->
    <div v-if="favOnly" class="fav-bar">
      <span class="fav-bar-info"><i class="ri-star-line"></i> 已收藏 {{ favCount }} 条精选 · 保存在本地浏览器</span>
      <span class="fav-bar-actions">
        <a class="back-link" @click="onExportFav" title="导出精选收藏为 JSON 文件"><i class="ri-download-2-line"></i> 导出</a>
        <a class="back-link" @click="$refs.selFavFile.click()" title="从 JSON 文件导入精选收藏"><i class="ri-upload-2-line"></i> 导入</a>
      </span>
      <input ref="selFavFile" type="file" accept=".json,application/json" style="display:none" @change="onImportFile" />
    </div>
    <div v-if="favMsg" class="fav-msg">{{ favMsg }}</div>

    <!-- 内容区 -->
    <div v-if="loading || dayLoading" class="loading">加载中…</div>
    <div v-else-if="error" class="error-tip">{{ error }}</div>
    <div v-else>
      <div v-if="!filteredItems.length" class="empty"><i class="ri-inbox-line"></i> 没有匹配的条目</div>
      <div
        v-for="(item, idx) in filteredItems"
        :key="idx"
        class="entry spotlight"
      >
        <div class="title"><a :href="item.url" target="_blank" rel="noopener">{{ item.title }}</a></div>
        <div class="subtitle" v-if="item.subtitle">{{ item.subtitle }}</div>
        <div class="meta">
          <span class="source">{{ item.source }}</span>
          <span class="heat" v-if="item.heat">{{ item.heat }}</span>
          <a
            class="sel-fav-btn"
            :class="{ 'kp-fav-active': isFaved(item) }"
            :title="isFaved(item) ? '取消收藏' : '收藏（保存在本地浏览器）'"
            @click.stop.prevent="toggleFav(item)"
          ><i :class="isFaved(item) ? 'ri-star-fill' : 'ri-star-line'"></i></a>
        </div>
      </div>
    </div>

    <!-- 页脚 -->
    <footer>
      <p>更新日期 {{ latestDate || '-' }} · 收录 {{ total }} 份精选</p>
      <p class="footer-links">
        <a href="/api/rss.xml" target="_blank" rel="noopener" title="RSS 订阅"><i class="ri-rss-line"></i> RSS 订阅</a>
      </p>
      <div class="beian">
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener">闽ICP备2026026930号-1</a>
        <br>
        <a href="https://beian.mps.gov.cn/#/query/webSearch?code=35010202002243" target="_blank" rel="noopener noreferrer">
          <img src="/static/beian-logo.png" style="vertical-align:middle;width:16px;height:16px;margin-right:4px;" alt="公安备案标识" />
          闽公网安备35010202002243号
        </a>
      </div>
    </footer>
  </div>
</template>

<script>
import aiPushSelected from '../api/aiPushSelected'
import { selFavState, selHas, selToggle, exportSelJson, importSelJson } from '../utils/favorites'

const WEEKDAYS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
const PILL_COUNT = 7

export default {
  name: 'SelectedPage',
  data () {
    return {
      loading: false,
      dayLoading: false,  // 切换日期时的加载状态（独立于首屏 loading）
      error: '',
      // 日期导航
      allDates: [],
      total: 0,
      selectedDate: '',
      dateInput: '',     // 日历跳转输入（type=date）
      // 当日精选详情
      currentTitle: '',
      currentItems: [],   // 当日全部条目（{title,subtitle,url,source,heat}）
      // 筛选
      activeSource: '全部',
      sourceList: [],
      keyword: '',
      sortBy: 'newest',
      favOnly: false,   // 只看收藏
      favMsg: '',       // 收藏导入/导出提示
      searchTimer: null
    }
  },
  computed: {
    recentPills () {
      return this.allDates.slice(0, PILL_COUNT)
    },
    minDate () {
      return this.allDates[this.allDates.length - 1] || ''
    },
    maxDate () {
      return this.allDates[0] || ''
    },
    isToday () {
      const today = this.formatYmd(new Date())
      return this.selectedDate === today
    },
    latestDate () {
      return this.allDates[0] || ''
    },
    latestDateShort () {
      return this.latestDate ? this.latestDate.slice(5) : ''
    },
    sourceCount () {
      return this.sourceList.length
    },
    /** 精选收藏数量（响应 selFavState 变化） */
    favCount () {
      return selFavState.items.length
    },
    filteredItems () {
      // 只看收藏：直接展示全部收藏记录（跨日期聚合，按收藏时间倒序）
      let arr = this.favOnly ? [...selFavState.items] : [...this.currentItems]
      if (this.activeSource !== '全部') {
        arr = arr.filter(d => d.source === this.activeSource)
      }
      const q = this.keyword.trim().toLowerCase()
      if (q) {
        arr = arr.filter(d =>
          (d.title || '').toLowerCase().includes(q) ||
          (d.subtitle || '').toLowerCase().includes(q)
        )
      }
      // 最热优先：按 heat 数字降序
      if (this.sortBy === 'hottest') {
        arr.sort((a, b) => this.toHeatNum(b) - this.toHeatNum(a))
      }
      return arr
    }
  },
  mounted () {
    this.init()
  },
  methods: {
    async init () {
      this.loading = true
      this.error = ''
      try {
        await this.loadDates()
        // 默认选最新日期
        if (this.allDates.length) {
          await this.selectDate(this.allDates[0])
        }
      } catch (e) {
        this.error = e.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    async loadDates () {
      const { data, total } = await aiPushSelected.dateList(60)
      this.allDates = data || []
      this.total = total || this.allDates.length
    },
    async loadDayDetail (pushDate) {
      const { data } = await aiPushSelected.getByDate(pushDate)
      if (!data) {
        this.currentTitle = ''
        this.currentItems = []
        this.sourceList = []
        return
      }
      this.currentTitle = data.title || ''
      this.currentItems = data.items || []
      // 提取来源列表
      const set = new Set()
      this.currentItems.forEach(d => { if (d.source) set.add(d.source) })
      this.sourceList = [...set]
    },
    async selectDate (d) {
      this.selectedDate = d
      this.activeSource = '全部'
      this.keyword = ''
      // 切换日期时显示加载动画
      this.dayLoading = true
      try {
        await this.loadDayDetail(d)
      } finally {
        this.dayLoading = false
      }
    },
    setSource (s) {
      this.activeSource = s
      this.rerender()
    },
    /** 精选条目是否已收藏 */
    isFaved (item) {
      return selHas(item.url)
    },
    toggleFav (item) {
      selToggle(item, this.selectedDate)
    },
    onExportFav () {
      if (!this.favCount) {
        this.flashFavMsg('还没有收藏精选，先点条目右侧的 ☆')
        return
      }
      exportSelJson()
      this.flashFavMsg('已导出 aid-favorites-selected.json')
    },
    async onImportFile (e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = '' // 允许重复选同一文件
      if (!file) return
      try {
        const added = await importSelJson(file)
        this.flashFavMsg(`导入成功：新增 ${added} 条精选收藏`)
      } catch (err) {
        this.flashFavMsg('导入失败：' + (err.message || '文件格式错误'))
      }
    },
    flashFavMsg (msg) {
      this.favMsg = msg
      clearTimeout(this._favMsgTimer)
      this._favMsgTimer = setTimeout(() => { this.favMsg = '' }, 3000)
    },
    onSearch () {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.rerender(), 200)
    },
    rerender () {
      // computed 自动响应
    },
    onDateInput () {
      // 保留所选值，让日历持续展示已选日期
      if (this.dateInput) {
        this.selectDate(this.dateInput)
      }
    },
    goToday () {
      const today = this.formatYmd(new Date())
      if (this.allDates.includes(today)) {
        this.selectDate(today)
      } else if (this.allDates.length) {
        this.selectDate(this.allDates[0])
      }
    },
    toHeatNum (item) {
      const m = (item.heat || '').match(/\d+/g)
      return m ? parseInt(m[0]) : 0
    },
    formatDatePill (d) {
      if (!d) return ''
      const dt = new Date(d.replace(/-/g, '/'))
      const m = String(dt.getMonth() + 1).padStart(2, '0')
      const day = String(dt.getDate()).padStart(2, '0')
      return `${m}-${day} ${WEEKDAYS[dt.getDay()]}`
    },
    formatDateFull (d) {
      if (!d) return ''
      const dt = new Date(d.replace(/-/g, '/'))
      return `${dt.getFullYear()}年${dt.getMonth() + 1}月${dt.getDate()}日 ${WEEKDAYS[dt.getDay()]}`
    },
    formatYmd (dt) {
      const y = dt.getFullYear()
      const m = String(dt.getMonth() + 1).padStart(2, '0')
      const d = String(dt.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }
  }
}
</script>
